import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, TagIcon, CalendarIcon, UserIcon, ChevronRightIcon } from 'lucide-react';
type BlogPost = {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  image: string;
  featured?: boolean;
};
export function Blog() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const blogPosts: BlogPost[] = [{
    id: 'esim-vs-physical-sim',
    title: 'eSIM vs Physical SIM: What Travelers Need to Know in 2023',
    excerpt: 'Discover the key differences between eSIM and physical SIM cards and why eSIMs are becoming the preferred choice for international travelers.',
    category: 'Technology',
    author: 'Sarah Johnson',
    date: 'May 15, 2023',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1585060544812-6b45742d762f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=80',
    featured: true
  }, {
    id: 'save-roaming-fees',
    title: 'How to Save Up to 90% on International Roaming Fees',
    excerpt: 'Learn practical strategies to avoid excessive roaming charges while traveling abroad, including how eSIMs can dramatically reduce your connectivity costs.',
    category: 'Travel Tips',
    author: 'Michael Chen',
    date: 'April 22, 2023',
    readTime: '4 min read',
    image: 'https://images.unsplash.com/photo-1425913397330-cf8af2ff40a1?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80'
  }, {
    id: 'uae-travel-guide',
    title: 'Ultimate UAE Travel Guide: Staying Connected in Dubai and Beyond',
    excerpt: 'Everything you need to know about mobile connectivity in the UAE, including why you must activate your eSIM before arrival and the best data plans for tourists.',
    category: 'Destinations',
    author: 'Fatima Al-Zahra',
    date: 'March 10, 2023',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80'
  }, {
    id: 'remote-work-connectivity',
    title: "he Digital Nomad's Guide to Global Connectivit",
    excerpt: 'How remote workers can stay connected reliably and affordably while traveling the world, with tips on choosing the right eSIM plans for different regions.',
    category: 'Remote Work',
    author: 'David Wilson',
    date: 'February 18, 2023',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&q=80'
  }];
  const categories = [...new Set(blogPosts.map(post => post.category))];
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) || post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory ? post.category === activeCategory : true;
    return matchesSearch && matchesCategory;
  });
  const featuredPost = blogPosts.find(post => post.featured);
  return <main className="py-16 w-full bg-[#F9FAFB]" data-id="element-731">
      <div className="container mx-auto px-4" data-id="element-732">
        <div className="text-center mb-12" data-id="element-733">
          <h1 className="text-4xl font-bold mb-4" data-id="element-734">TravelSIM Blog</h1>
          <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-735">
            Insights, tips, and guides to help you stay connected while
            traveling the world.
          </p>
        </div>
        {/* Search and Categories */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-6" data-id="element-736">
          <div className="relative w-full md:w-auto md:min-w-[320px]" data-id="element-737">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-738">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-739" />
            </div>
            <input type="text" placeholder="Search articles..." className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-740" />
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-741">
            <button className={`px-4 py-2 rounded-full text-sm font-medium ${activeCategory === null ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} onClick={() => setActiveCategory(null)} data-id="element-742">
              All
            </button>
            {categories.map(category => <button key={category} className={`px-4 py-2 rounded-full text-sm font-medium ${activeCategory === category ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} onClick={() => setActiveCategory(category)} data-id="element-743">
                {category}
              </button>)}
          </div>
        </div>
        {/* Featured Post */}
        {featuredPost && !activeCategory && !searchQuery && <div className="mb-12" data-id="element-744">
            <Link to={`/blog/${featuredPost.id}`} className="block" data-id="element-745">
              <div className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-id="element-746">
                <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-747">
                  <div className="h-64 md:h-auto overflow-hidden" data-id="element-748">
                    <img src={featuredPost.image} alt={featuredPost.title} className="w-full h-full object-cover" data-id="element-749" />
                  </div>
                  <div className="p-8 flex flex-col justify-center" data-id="element-750">
                    <div className="flex items-center mb-4" data-id="element-751">
                      <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full" data-id="element-752">
                        Featured
                      </span>
                      <span className="mx-2 text-gray-300" data-id="element-753">•</span>
                      <span className="text-sm text-gray-500 flex items-center" data-id="element-754">
                        <TagIcon className="h-4 w-4 mr-1" data-id="element-755" />
                        {featuredPost.category}
                      </span>
                    </div>
                    <h2 className="text-2xl font-bold mb-3 hover:text-blue-600 transition-colors" data-id="element-756">
                      {featuredPost.title}
                    </h2>
                    <p className="text-gray-600 mb-4" data-id="element-757">{featuredPost.excerpt}</p>
                    <div className="flex items-center text-sm text-gray-500" data-id="element-758">
                      <span className="flex items-center" data-id="element-759">
                        <UserIcon className="h-4 w-4 mr-1" data-id="element-760" />
                        {featuredPost.author}
                      </span>
                      <span className="mx-2 text-gray-300" data-id="element-761">•</span>
                      <span className="flex items-center" data-id="element-762">
                        <CalendarIcon className="h-4 w-4 mr-1" data-id="element-763" />
                        {featuredPost.date}
                      </span>
                      <span className="mx-2 text-gray-300" data-id="element-764">•</span>
                      <span data-id="element-765">{featuredPost.readTime}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>}
        {/* Blog Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12" data-id="element-766">
          {filteredPosts.filter(post => !post.featured || activeCategory || searchQuery).map(post => <Link key={post.id} to={`/blog/${post.id}`} className="block" data-id="element-767">
                <div className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow h-full flex flex-col" data-id="element-768">
                  <div className="h-48 overflow-hidden" data-id="element-769">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform hover:scale-105 duration-300" data-id="element-770" />
                  </div>
                  <div className="p-6 flex-1 flex flex-col" data-id="element-771">
                    <div className="flex items-center mb-3" data-id="element-772">
                      <span className="text-sm text-gray-500 flex items-center" data-id="element-773">
                        <TagIcon className="h-4 w-4 mr-1" data-id="element-774" />
                        {post.category}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold mb-3 hover:text-blue-600 transition-colors" data-id="element-775">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4 flex-1" data-id="element-776">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500" data-id="element-777">
                      <span className="flex items-center" data-id="element-778">
                        <UserIcon className="h-4 w-4 mr-1" data-id="element-779" />
                        {post.author}
                      </span>
                      <span className="flex items-center" data-id="element-780">
                        <CalendarIcon className="h-4 w-4 mr-1" data-id="element-781" />
                        {post.date}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>)}
        </div>
        {filteredPosts.length === 0 && <div className="text-center py-12 bg-white rounded-xl shadow-sm" data-id="element-782">
            <h3 className="text-xl font-semibold mb-2" data-id="element-783">No articles found</h3>
            <p className="text-gray-600" data-id="element-784">
              Try adjusting your search or category filter to find what you're
              looking for.
            </p>
          </div>}
        {/* Newsletter Subscription */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-8 text-white mb-12" data-id="element-785">
          <div className="max-w-3xl mx-auto text-center" data-id="element-786">
            <h2 className="text-2xl font-bold mb-4" data-id="element-787">Stay Connected</h2>
            <p className="text-blue-100 mb-6" data-id="element-788">
              Subscribe to our newsletter for the latest travel connectivity
              tips, exclusive offers, and eSIM updates.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" data-id="element-789">
              <input type="email" placeholder="Your email address" className="px-4 py-3 rounded-lg border-0 flex-1 text-gray-900" data-id="element-790" />
              <button className="bg-white text-blue-600 font-medium px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-791">
                Subscribe
              </button>
            </div>
          </div>
        </div>
        {/* Popular Categories */}
        <div className="mb-12" data-id="element-792">
          <h2 className="text-2xl font-bold mb-6" data-id="element-793">Explore by Category</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-794">
            {categories.map(category => <div key={category} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:border-blue-200 transition-colors cursor-pointer" onClick={() => setActiveCategory(category)} data-id="element-795">
                <h3 className="font-semibold text-lg mb-2 flex items-center justify-between" data-id="element-796">
                  {category}
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" data-id="element-797" />
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-798">
                  {blogPosts.filter(post => post.category === category).length}{' '}
                  articles
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </main>;
}